import * as _ from 'lodash';
import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { SurveyForm } from 'src/app/shared/models/survey/surveyForm';
import { SurveyService } from 'src/app/shared/services/survey.service';
import { Subscription } from 'rxjs';
import { AttributesEditorDataSource } from 'src/app/shared/components/attributes-editor/attributesEditorDataSource';
import { LocalData, LocalPersistenceService } from '../../local-persistence.service';
import { BuildInfoCheckService } from 'src/app/shared/services/build-info-check.service';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'survey-respond',
  templateUrl: './survey-respond.component.html',
  styleUrls: ['./survey-respond.component.scss'],
})
export class SurveyRespondComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  editorDataSource = new AttributesEditorDataSource();
  isSaving: boolean = false;
  survey: SurveyForm;
  secureLinkId: string;
  @Input()
  inPreviewMode: boolean = false;
  @Output()
  previewSurveySave = new EventEmitter();
  isHeaderButtonVisible: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private surveyService: SurveyService,
    private localPersistenceService: LocalPersistenceService,
    private buildInfoCheckService: BuildInfoCheckService
  ) {}

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }

  ngOnInit() {
    if (!this.inPreviewMode) {
      this.subscriptions.push(
        this.surveyService.survey.subscribe((result) => {
          if (result) {
            this.surveyService.setSurveyForm();
            this.survey = result;
            let attributes = result.attributes;
            const localData = this.localPersistenceService.getLocalData<LocalData<null>>(
              this.surveyService.secureLinkId
            );
            let loadedAttributes =
              localData && localData.answers
                ? LocalPersistenceService.buildFormUsingSavedAnswers(
                    localData.answers,
                    this.survey.questions
                  )
                : [];
            attributes = attributes.concat(loadedAttributes);
            this.editorDataSource.sortOnName = false;
            this.editorDataSource.load(attributes, this.survey.questions);
            this.setupHeaderButton();
            this.buildInfoCheckService.initBuildFileCheck(
              "Don't worry, you won't lose your progress!"
            );
          }
        }),
        this.buildInfoCheckService.onReloadPrompt.subscribe(() => {
          this.localPersistenceService.saveDataForLater(
            this.buildLocalData(),
            this.surveyService.secureLinkId,
            false
          );
        })
      );
    } else {
      this.subscriptions.push(
        this.surveyService.surveyPreview.subscribe((result) => {
          if (result) {
            this.survey = result;
            this.editorDataSource.sortOnName = false;
            this.editorDataSource.load([], this.survey.questions);
          }
        })
      );
    }
  }

  isSurveyValid(): boolean {
    return this.editorDataSource.isValid;
  }
  public saveSurvey(): void {
    if (this.isSurveyValid() && !this.isSaving) {
      this.isSaving = true;
      if (this.inPreviewMode) {
        this.previewSurveySave.emit();
        return;
      }
      this.surveyService.setIsLoadingOrSaving(true);
      this.surveyService.updateSurvey(this.editorDataSource.asAttributeWrites());
      this.localPersistenceService.deleteLocalData(this.surveyService.secureLinkId);
    }
  }
  downloadFile(event: { fileName: string; attributeDefinitionId: string }) {
    let secureLinkId = this.route.parent.snapshot?.params['secureLinkId'];

    let requestUrl = `${secureLinkId}/${event.attributeDefinitionId}/document`;
    this.surveyService.downloadFile(requestUrl, event.fileName);
  }
  private setupHeaderButton() {
    if (this.isHeaderButtonVisible) return;
    this.localPersistenceService.buttonSubject.next({
      label: 'Save For Later',
      action: () =>
        this.localPersistenceService.saveDataForLater(
          this.buildLocalData(),
          this.surveyService.secureLinkId
        ),
      tooltip:
        'Your responses will be saved for 15 days. You must use the same device and browser when ' +
        "coming back. You must still submit responses once you're ready.",
    });
    this.isHeaderButtonVisible = true;
  }

  private buildLocalData(): LocalData<null> {
    return {
      reference: null,
      answers: this.localPersistenceService.getAnswers(this.editorDataSource),
    };
  }
}
